import { jsPDF } from "jspdf";

export const RESUME_CONFIG = {
  page: {
    unit: "mm",
    format: "a4",
    orientation: "portrait",
    margins: { top: 15, right: 7, bottom: 15, left: 7 },
  },
  fonts: {
    header: { style: "times", weight: "bold", size: 20, color: "#000000" },
    sectionHeader: {
      style: "times",
      weight: "bold",
      size: 12,
      color: "#000000",
    },
    sectionTitle: {
      style: "times",
      weight: "bold",
      size: 11,
      color: "#000000",
    },
    normal: { style: "times", weight: "normal", size: 11, color: "#000000" },
    small: { style: "times", weight: "bolditalic", size: 9, color: "#000000" },
  },
  spacing: {
    sectionGap: 3,
    headerGap: 6,
    lineGap: 5,
    paragraphGap: 2,
    indentation: 5,
    skillIndentation: 42,
  },
  formatting: { textAlign: { header: "center" }, bulletStyle: "•" },
  divider: { style: "line", color: "#000000", width: 0.2, spacing: 1 },
  sections: {
    header: {
      contactSeparator: " | ",
      icons: {
        size: 3,
        spacing: 1,
        contactSpacing: 4,
        verticalOffset: 0.75,
        urls: {
          email: "https://cdn-icons-png.flaticon.com/128/712/712040.png",
          phone: "https://cdn-icons-png.flaticon.com/128/25/25377.png",
          linkedin: "https://cdn-icons-png.flaticon.com/128/61/61109.png",
          github: "https://cdn-icons-png.flaticon.com/128/733/733609.png",
        },
      },
    },
  },
};

export class ResumeBuilder {
  constructor(config = RESUME_CONFIG) {
    this.config = config;
    this.initDocument();
  }

  initDocument() {
    this.doc = new jsPDF({
      unit: this.config.page.unit,
      format: this.config.page.format,
      orientation: this.config.page.orientation,
    });
    this.currentY = this.config.page.margins.top;
    this.pageWidth = this.doc.internal.pageSize.width;
    this.contentWidth =
      this.pageWidth -
      (this.config.page.margins.left + this.config.page.margins.right);
  }

  safeText(text, x, y, options) {
    this.doc.text(text || "", x, y, options);
  }

  setFont(type) {
    const font = this.config.fonts[type];
    this.doc.setFont(font.style, font.weight);
    this.doc.setFontSize(font.size);
    this.doc.setTextColor(font.color);
  }

  checkAndAddPage() {
    if (
      this.currentY + this.config.page.margins.bottom >
      this.doc.internal.pageSize.height
    ) {
      this.doc.addPage();
      this.currentY = this.config.page.margins.top;
    }
  }

  addDivider() {
    this.checkAndAddPage();
    this.doc.setDrawColor(this.config.divider.color);
    this.doc.setLineWidth(this.config.divider.width);
    this.doc.line(
      this.config.page.margins.left,
      this.currentY,
      this.pageWidth - this.config.page.margins.right,
      this.currentY
    );
    this.currentY += this.config.divider.spacing;
  }

  addSection(title, content) {
    this.checkAndAddPage();
    this.setFont("sectionHeader");
    this.safeText(
      title.toUpperCase(),
      this.config.page.margins.left,
      this.currentY
    );
    this.currentY += this.config.spacing.sectionGap;
    if (this.config.divider.style === "line") this.addDivider();
    if (content) {
      this.currentY += this.config.spacing.paragraphGap;

      this.setFont("normal");
      const lines = this.doc.splitTextToSize(
        content || "",
        this.config.formatting.maxLineWidth || this.contentWidth
      );
      lines.forEach((line) => {
        this.checkAndAddPage();
        this.safeText(line, this.config.page.margins.left, this.currentY);
        this.currentY += this.config.spacing.lineGap;
      });
    }
    this.currentY += this.config.spacing.sectionGap;
  }

  addHeader(name, contacts) {
    const headerConfig = this.config.sections.header;
    const iconConfig = headerConfig.icons;

    this.setFont("header");
    this.checkAndAddPage();
    this.safeText(name, this.pageWidth / 2, this.currentY, { align: "center" });
    this.currentY += this.config.spacing.headerGap;

    if (contacts && contacts.length > 0) {
      this.checkAndAddPage();
      this.setFont("small");

      const contactsWithData = contacts
        .map((c) => {
          if (c.includes("@"))
            return { text: c, key: "email", url: `mailto:${c}` };
          if (/[\d-+()]{7,}/.test(c))
            return {
              text: c,
              key: "phone",
              url: `tel:${c.replace(/\s+/g, "")}`,
            };
          if (c.includes("linkedin"))
            return {
              text: c.replace(/https?:\/\/(www\.)?/, ""),
              key: "linkedin",
              url: c.startsWith("http") ? c : `https://${c}`,
            };
          if (c.includes("github"))
            return {
              text: c.replace(/https?:\/\/(www\.)?/, ""),
              key: "github",
              url: c.startsWith("http") ? c : `https://${c}`,
            };
          return null;
        })
        .filter(Boolean);

      let totalWidth = 0;
      contactsWithData.forEach((item) => {
        totalWidth +=
          this.doc.getTextWidth(item.text) +
          iconConfig.contactSpacing +
          iconConfig.size +
          iconConfig.spacing;
      });
      totalWidth -= iconConfig.contactSpacing;

      let currentX = (this.pageWidth - totalWidth) / 2;

      contactsWithData.forEach((item, index) => {
        try {
          this.doc.addImage(
            iconConfig.urls[item.key],
            "PNG",
            currentX,
            this.currentY - iconConfig.size * 0.8,
            iconConfig.size,
            iconConfig.size
          );
        } catch (e) {
          console.error("Could not add image for:", item.key);
        }

        currentX += iconConfig.size + iconConfig.spacing;
        const textWidth = this.doc.getTextWidth(item.text);
        this.safeText(item.text, currentX, this.currentY);
        this.doc.link(currentX, this.currentY - 3, textWidth, 4, {
          url: item.url,
        });
        currentX += textWidth + iconConfig.contactSpacing;
      });

      this.currentY += this.config.spacing.headerGap;
      this.currentY += this.config.spacing.headerGap;
    }
  }

  addProfessionalSummary(summary) {
    if (!summary || summary.trim() === "") return;
    this.addSection("PROFESSIONAL SUMMARY", summary);
  }

  addEducation(school, degree, duration, location, gpa, scoreType) {
    this.checkAndAddPage();
    this.setFont("sectionTitle");

    const durationWidth = this.doc.getTextWidth(duration || "");
    this.safeText(school, this.config.page.margins.left, this.currentY);
    if (duration) {
      this.safeText(
        duration,
        this.pageWidth - this.config.page.margins.right - durationWidth,
        this.currentY
      );
    }
    this.currentY += this.config.spacing.lineGap;

    this.checkAndAddPage();
    this.setFont("small");

    const locationWidth = this.doc.getTextWidth(location || "");
    this.safeText(degree, this.config.page.margins.left, this.currentY);
    if (location) {
      this.safeText(
        location,
        this.pageWidth - this.config.page.margins.right - locationWidth,
        this.currentY
      );
    }

    if (gpa) {
      const scoreLabel = scoreType ? `${scoreType}: ` : "CGPA: ";
      const gpaText = ` | ${scoreLabel}${gpa}`;
      const degreeWidth = this.doc.getTextWidth(degree || "");
      this.safeText(
        gpaText,
        this.config.page.margins.left + degreeWidth,
        this.currentY
      );
    }

    this.currentY +=
      this.config.spacing.lineGap + this.config.spacing.paragraphGap;
  }

  addGeneralSection(sectionTitle, entries) {
    if (!entries || entries.length === 0) return;
    this.addSection(sectionTitle.toUpperCase(), null);

    entries.forEach((entry) => {
      const { title, subtitle, duration, location, bullets, tags } = entry;

      this.checkAndAddPage();
      this.setFont("sectionTitle");
      const durationWidth = this.doc.getTextWidth(duration || "");
      this.safeText(title, this.config.page.margins.left, this.currentY);
      if (duration) {
        this.safeText(
          duration,
          this.pageWidth - this.config.page.margins.right - durationWidth,
          this.currentY
        );
      }
      this.currentY += this.config.spacing.lineGap;

      if (subtitle || location) {
        this.checkAndAddPage();
        this.setFont("small");
        const locationWidth = this.doc.getTextWidth(location || "");
        if (subtitle)
          this.safeText(subtitle, this.config.page.margins.left, this.currentY);
        if (location)
          this.safeText(
            location,
            this.pageWidth - this.config.page.margins.right - locationWidth,
            this.currentY
          );
        this.currentY += this.config.spacing.lineGap;
      }

      if (bullets && bullets.length > 0) {
        bullets.forEach((bullet) => {
          if (!bullet) return;
          this.checkAndAddPage();

          this.setFont("normal");
          this.safeText(
            this.config.formatting.bulletStyle,
            this.config.page.margins.left,
            this.currentY
          );

          const startX =
            this.config.page.margins.left + this.config.spacing.indentation;
          let currentX = startX;
          const endX = this.pageWidth - this.config.page.margins.right;

          const tagArray = Array.isArray(tags)
            ? tags.map((t) => t.trim())
            : (tags || "").split(",").map((t) => t.trim());
          const sortedTags = tagArray
            .filter((tag) => typeof tag === "string" && tag.length > 0)
            .map((tag) => tag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
            .sort((a, b) => b.length - a.length);

          const textToProcess = bullet.trim();
          const parts =
            sortedTags.length > 0
              ? textToProcess.split(
                  new RegExp(`(${sortedTags.join("|")})`, "gi")
                )
              : [textToProcess];

          parts.forEach((part, partIndex) => {
            if (!part) return;

            const isTag = tagArray.some(
              (tag) => part.toLowerCase() === tag.toLowerCase()
            );
            this.doc.setFont(
              this.doc.getFont().fontName,
              isTag ? "bold" : "normal"
            );

            let words = part.split(" ");
            words.forEach((word, index) => {
              if (!word) return;

              let wordToPrint = word;

              if (index < words.length - 1 || partIndex < parts.length - 1) {
                wordToPrint += " ";
              }

              const wordWidth = this.doc.getTextWidth(wordToPrint);

              if (currentX + wordWidth > endX && currentX > startX) {
                currentX = startX;
                this.currentY += this.config.spacing.lineGap;
                this.checkAndAddPage();
              }

              this.safeText(wordToPrint, currentX, this.currentY);
              currentX += wordWidth;
            });
          });

          this.currentY += this.config.spacing.lineGap;
        });
      }

      this.currentY += this.config.spacing.paragraphGap;
    });
  }

  addSkills(skillsObject) {
    if (!skillsObject || Object.keys(skillsObject).length === 0) return;
    this.addSection("SKILLS", null);
    const skillStartX =
      this.config.page.margins.left + this.config.spacing.skillIndentation;

    Object.entries(skillsObject).forEach(([category, skills]) => {
      if (!skills || skills.length === 0) return;
      this.checkAndAddPage();
      this.setFont("normal");

      const startY = this.currentY;

      const categoryText = `${category}: `;
      const categoryColumnWidth =
        skillStartX - this.config.page.margins.left - 2;
      this.doc.setFont(this.doc.getFont().fontName, "bold");
      const categoryLines = this.doc.splitTextToSize(
        categoryText,
        categoryColumnWidth
      );
      categoryLines.forEach((line, index) => {
        this.safeText(
          line,
          this.config.page.margins.left,
          startY + index * this.config.spacing.lineGap
        );
      });
      const categoryHeight = categoryLines.length * this.config.spacing.lineGap;

      const skillsText = Array.isArray(skills)
        ? skills.join(", ")
        : String(skills);
      const skillsAvailableWidth =
        this.pageWidth - this.config.page.margins.right - skillStartX;
      this.doc.setFont(this.doc.getFont().fontName, "normal");
      const skillsLines = this.doc.splitTextToSize(
        skillsText,
        skillsAvailableWidth
      );
      skillsLines.forEach((line, index) => {
        this.safeText(
          line,
          skillStartX,
          startY + index * this.config.spacing.lineGap
        );
      });
      const skillsHeight = skillsLines.length * this.config.spacing.lineGap;

      this.currentY = startY + Math.max(categoryHeight, skillsHeight);
      this.currentY -= this.config.spacing.lineGap;
      this.currentY += this.config.spacing.lineGap;
    });

    this.currentY += this.config.spacing.sectionGap - 1;
  }

  renderContent(content, sectionOrder) {
    this.currentY = this.config.page.margins.top;
    (sectionOrder || []).forEach((section) => {
      switch (section.toLowerCase()) {
        case "header":
          if (content.header)
            this.addHeader(content.header.name, content.header.contacts);
          break;
        case "summary":
          if (content.summary) this.addProfessionalSummary(content.summary);
          break;
        case "education":
          if (content.education && content.education.length > 0) {
            this.addSection("EDUCATION", null);
            content.education.forEach((edu) => {
              this.addEducation(
                edu.school,
                edu.degree,
                edu.duration,
                edu.location,
                edu.gpa,
                edu.scoreType
              );
            });
          }
          break;
        case "skills":
          if (content.skills && Object.keys(content.skills).length > 0)
            this.addSkills(content.skills);
          break;
        default:
          if (
            content[section] &&
            Array.isArray(content[section]) &&
            content[section].length > 0
          ) {
            this.addGeneralSection(section.toUpperCase(), content[section]);
          }
          break;
      }
    });
  }

  getDataUrl() {
    return this.doc.output("datauristring");
  }
  save(filename) {
    this.doc.save(filename);
  }
}
